import { authedFetch } from './authedFetch';
import { getAccessToken } from './authSession';

const DEVICE_ID_KEY = 'shadowland_mobile_device_id';
const LOCK_STATE_KEY = 'shadowland_mobile_lock_state';

function storage() {
  if (typeof window === 'undefined') return null;
  return window.localStorage || null;
}

function randomId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getMobileDeviceId() {
  const s = storage();
  if (!s) return randomId();
  try {
    const existing = s.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const next = randomId();
    s.setItem(DEVICE_ID_KEY, next);
    return next;
  } catch {
    return randomId();
  }
}

export function clearMobileSessionState() {
  try { storage()?.removeItem(LOCK_STATE_KEY); } catch { /* ignore unavailable storage */ }
}

export function setMobileAppLocked(locked, reason = '') {
  const payload = locked
    ? { locked: true, reason, lockedAt: new Date().toISOString() }
    : { locked: false, unlockedAt: new Date().toISOString() };
  try { storage()?.setItem(LOCK_STATE_KEY, JSON.stringify(payload)); } catch { /* ignore unavailable storage */ }
  return payload;
}

function jwtPayload(token) {
  try {
    const [, payload] = String(token || '').split('.');
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = typeof atob === 'function'
      ? atob(normalized)
      : '';
    return decoded ? JSON.parse(decoded) : {};
  } catch {
    return {};
  }
}

function platformName() {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'web';
}

function deviceName() {
  if (typeof navigator === 'undefined') return '';
  return [navigator.platform, navigator.userAgentData?.platform].filter(Boolean)[0] || '';
}

function appVersion() {
  return import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_COMMIT_SHA || '';
}

function sessionPayload(extra = {}) {
  return {
    deviceId: getMobileDeviceId(),
    platform: platformName(),
    deviceName: deviceName(),
    appVersion: appVersion(),
    locale: typeof navigator !== 'undefined' ? navigator.language || '' : '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    biometricAvailable: extra.biometricAvailable === true,
    biometricEnabled: extra.biometricEnabled === true,
    ...extra,
  };
}

export function isMobileSessionRevokedError(error) {
  return error?.code === 'MOBILE_SESSION_REVOKED'
    || /mobile session revoked/i.test(String(error?.message || ''));
}

async function mobileSessionRequest(method, body) {
  const response = await authedFetch('/api/auth/mobile-session', {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: method === 'POST' ? JSON.stringify(body || {}) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 401 && data?.code === 'MOBILE_SESSION_REVOKED') {
    const err = new Error(data.error || 'Mobile session revoked');
    err.code = 'MOBILE_SESSION_REVOKED';
    err.data = data;
    throw err;
  }
  if (!response.ok && response.status !== 202) {
    throw new Error(data?.error || 'Mobile session request failed');
  }
  return data;
}

export async function registerMobileSession(extra = {}) {
  const token = await getAccessToken({ timeoutMs: 2500 });
  if (!token) return { skipped: true, reason: 'no-session' };
  const claims = jwtPayload(token);
  return mobileSessionRequest('POST', sessionPayload({
    action: 'register',
    authSessionId: claims.session_id || claims.sid || '',
    ...extra,
  }));
}

export async function lockMobileSession(reason = 'inactivity') {
  setMobileAppLocked(true, reason);
  return mobileSessionRequest('POST', sessionPayload({ action: 'lock', reason }));
}

export async function unlockMobileSession(extra = {}) {
  setMobileAppLocked(false);
  return mobileSessionRequest('POST', sessionPayload({ action: 'unlock', ...extra }));
}

